// Operations related to sources associated with records (people, organizations, events)
// ie. on record_sources table or on associated external systems
// (pipedrive, airtable, mailchimp, luma, hubspot)

import { graphql } from "@/gql"

// Unified sources queries for all record types

// Fetch source_types for many records in one shot
export const fetchSourcesForRecords = graphql(`
  query fetchSourcesForRecords($recordIds: [Int!]!) {
    sources: record_sources(where: { record_id: { _in: $recordIds } }) {
      record_id
      source_type
    }
  }
`)

// Read the cached context for a (source_type, source_id) — used by source cards
// that only know the URN, not the owning record. Newest snapshot wins.
export const fetchSourceContext = graphql(`
  query fetchSourceContext($sourceType: String!, $sourceId: String!) {
    record_sources(
      where: { source_type: { _eq: $sourceType }, source_id: { _eq: $sourceId } }
      order_by: { updated_at: desc_nulls_last }
      limit: 1
    ) {
      id
      context
    }
  }
`)

// System-specific source queries and mutations

export const searchOrganizationSources = graphql(`
  query SearchOrganizationSources($query: String!, $sanity_query: String!) {
    airtable: airtable_organizations(
      where: {
        _or: [{ company_name: { _ilike: $query } }, { short_description: { _ilike: $query } }]
      }
      limit: 10
    ) {
      source_id: id
      name: company_name
      description: short_description
    }
    pipedrive: pipedrive_organization(where: { name: { _ilike: $query } }, limit: 10) {
      source_id: id
      name
      description: custom_short_description
    }
    hubspot: hubspot_company(
      where: {
        _or: [{ property_name: { _ilike: $query } }, { property_domain: { _ilike: $query } }]
      }
      limit: 10
    ) {
      source_id: id
      name: property_name
      description: property_description
    }
    sanity: sanity_allOrganization(where: { name: { matches: $sanity_query } }) {
      source_id: _id
      name
      description
    }
    standard_metrics: standard_metrics_company(
      where: {
        _and: [
          { _fivetran_deleted: { _eq: false } }
          { _or: [{ name: { _ilike: $query } }, { slug: { _ilike: $query } }] }
        ]
      }
      limit: 10
    ) {
      source_id: id
      name
      description: sector
    }
  }
`)

// Search sources across different systems
export const searchPersonSources = graphql(`
  query searchSources($query: String!, $sanity_query: String!) {
    airtable: airtable_people(
      where: {
        _or: [
          { name: { _ilike: $query } }
          { primary_email: { _ilike: $query } }
          { secondary_email: { _ilike: $query } }
        ]
      }
      limit: 50
    ) {
      source_id: id
      name
      email: primary_email
    }

    pipedrive: pipedrive_person(
      where: { _or: [{ name: { _ilike: $query } }, { email: { _ilike: $query } }] }
      limit: 50
    ) {
      source_id: id
      name
      email
    }

    mailchimp: mailchimp_member(
      where: { _or: [{ full_name: { _ilike: $query } }, { email_address: { _ilike: $query } }] }
      limit: 50
    ) {
      source_id: id
      email: email_address
      name: full_name
    }

    luma: luma_event_guest(
      where: { _or: [{ user_name: { _ilike: $query } }, { user_email: { _ilike: $query } }] }
      limit: 50
    ) {
      source_id: user_api_id
      email: user_email
      name: user_name
    }

    hubspot: hubspot_contact(
      where: { _or: [{ full_name: { _ilike: $query } }, { email: { _ilike: $query } }] }
      limit: 50
    ) {
      source_id: id
      vid
      name: full_name
      email
    }

    pipedrive_user(where: { name: { _ilike: $query } }, limit: 50) {
      source_id: id
      name
      email
    }

    sanity: sanity_allPerson(where: { name: { matches: $sanity_query } }, limit: 50) {
      source_id: _id
      name
    }
  }
`)

export const searchEventSources = graphql(`
  query searchEventSources($query: String!, $sanity_query: String!) {
    luma: luma_event(where: { name: { _ilike: $query } }, limit: 50) {
      source_id: id
      name
      description
    }

    airtable: airtable_projects(where: { project: { _ilike: $query } }, limit: 50) {
      source_id: id
      name: project
    }

    airtable_global: airtable_global_events_tracker(
      where: { event: { _ilike: $query } }
      limit: 50
    ) {
      source_id: id
      name: event
      description: event_description
    }

    sanity: sanity_allEvent(where: { title: { matches: $sanity_query } }, limit: 50) {
      source_id: _id
      name: title
    }

    sanity_devguild: sanity_allDevGuild(where: { title: { matches: $sanity_query } }, limit: 50) {
      source_id: _id
      name: title
    }
  }
`)

// Pipedrive specific queries
export const fetchPipedrivePersonSource = graphql(`
  query fetchPipedrivePersonSource($id: bigint!) {
    source: pipedrive_person_by_pk(id: $id) {
      active_flag
      cc_email
      created_at
      custom_airtable_id
      custom_github_url
      custom_labels
      custom_linked_in_url
      custom_twitter_url
      email
      id
      label
      name
      org_id
      owner_id
      phone
      picture_id
      update_time
      visible_to
      organization {
        custom_airtable_id
        custom_company_stage
        custom_is_hb_member
        custom_linked_in_url
        custom_long_description
        custom_member_number
        custom_pitchbook_id
        custom_short_description
        custom_twitter_url
        custom_url
        custom_website
        name
        owner_id
      }
    }
  }
`)

export const fetchPipedriveUser = graphql(`
  query fetchPipedriveUser($id: bigint!) {
    user: pipedrive_user_by_pk(id: $id) {
      id
      name
      email
      icon_url
      phone
      assigned_activities(where: { done: { _eq: false } }, order_by: { due_date: desc }) {
        id
        subject
        due_date
      }
    }
  }
`)

export const fetchPipedriveOrganizationSource = graphql(`
  query fetchPipedriveOrganizationSource($id: bigint!) {
    source: pipedrive_organization_by_pk(id: $id) {
      active_flag
      address
      address_admin_area_level_1
      address_admin_area_level_2
      address_country
      address_formatted_address
      address_locality
      address_postal_code
      address_route
      address_street_number
      address_sublocality
      address_subpremise
      cc_email
      created_at
      custom_airtable_id
      custom_annual_revenue
      custom_company_stage
      custom_email_domains
      custom_founded
      custom_gdrive_folder_id
      custom_heavybit_fund_s_
      custom_industry
      custom_is_hb_member
      custom_labels
      custom_linked_in_profile
      custom_linked_in_url
      custom_long_description
      custom_member_number
      custom_no_of_employees
      custom_number_of_employees
      custom_pitchbook_id
      custom_primary_contact
      custom_reporting_contact
      custom_segment
      custom_short_description
      custom_tags
      custom_twitter_followers
      custom_twitter_handle
      custom_twitter_url
      custom_url
      custom_website
      id
      label
      name
      owner_id
      update_time
      visible_to
    }
  }
`)

// Airtable specific queries
export const fetchAirtablePersonSource = graphql(`
  query fetchAirtablePersonSource($id: String!) {
    source: airtable_people_by_pk(id: $id) {
      id
      name
      email: primary_email
      primary_email
      secondary_email
      linkedin_url: social_linked_in_profile
      twitter_url: social_twitter_profile
      bio
      current_company
      preferred_headshot
    }
  }
`)

export const fetchAirtableOrganizationSource = graphql(`
  query fetchAirtableOrganizationSource($id: String!) {
    source: airtable_organizations_by_pk(id: $id) {
      additional_partner
      agm_invite_affiliation
      airtable_id_company
      careers_page_url
      cohorts
      company_blog_rss
      company_domain
      company_logo
      company_name
      company_segment
      company_stage
      company_website
      conference_host
      conference_tracker_manual_selections_from_company_segment_
      confirmed_events
      current_employees
      current_lead_partner
      days_since_onboarding
      event_sponsorship_target
      founder_s_
      founder_time_zones
      fundraising_announcement
      google_drive_folder_id
      heavybit_board_seat
      id
      internal_most_recent_fireflies_id
      internal_platform_support_flag
      internal_platform_support_flag_context
      internal_portco_runway
      internal_portco_runway_last_updated
      internal_portco_traffic_light
      internal_portco_traffic_light_updated
      job_listing_url
      last_scheduled_partner_call
      linked_in_profile_url
      logo_in_member_logo_deck
      long_description
      meeting_cadence
      member_number
      member_onboarding_date
      misc_organization_notes
      offered_platform_events
      one_pager
      organization_record_last_modified
      partner_hours_year
      perks_discounts_perk_provider
      pipedrive_id_company
      platform_activities_completed
      portfolio_company_status
      portfolio_platform_status
      portfolio_support_objectives
      previous_employees
      proposed_lead_partner
      reusable_partner_meeting_zoom
      scheduling_status
      short_description
      suggested_platform_event_opportunities
      twitter_profile_url
    }
  }
`)

// Mailchimp specific queries
export const fetchMailchimpPersonSource = graphql(`
  query fetchMailchimpPersonSource($id: String!) {
    source: mailchimp_member(where: { id: { _eq: $id } }) {
      id
      email_address
      unique_email_id
      full_name
      status
      source
      ip_signup
      timestamp_signup
      ip_opt
      timestamp_opt
      language
      last_changed
      list_id
      list {
        name
      }
    }
  }
`)

// Luma specific queries
export const fetchLumaPersonSource = graphql(`
  query fetchLumaPersonSource($id: String!) {
    source: luma_event_guest(where: { user_api_id: { _eq: $id } }) {
      id: user_api_id
      name: user_name
      email: user_email
      description: event {
        name
      }
      user_api_id
      user_email
      user_name
      created_at
      invited_at
      checked_in_at
      approval_status
      event {
        name
      }
    }
  }
`)

export const fetchLumaEventSource = graphql(`
  query fetchLumaEventSource($id: String!) {
    source: luma_event_by_pk(id: $id) {
      id
      name
      description
      url
      cover_url
      visibility
      start_at
      end_at
      duration_interval
      guests {
        id
        user_api_id
        name: user_name
        email: user_email
        status: approval_status
        invited_at
        registered_at
        checked_in_at
      }
    }
  }
`)

export const fetchSlackPersonSource = graphql(`
  query fetchSlackPerson($id: String!) {
    source: slack_users_by_pk(id: $id) {
      id
      name
      profile_image_72
      profile_phone
      profile_team
      profile_title
      profile_email
      profile_fields
      real_name
    }
  }
`)

// HubSpot specific queries
export const fetchHubSpotPersonSource = graphql(`
  query fetchHubSpotPersonSource($id: bigint!) {
    source: hubspot_contact_by_pk(id: $id) {
      company
      email
      full_name
      id
      vid
    }
  }
`)

export const fetchHubSpotOrganizationSource = graphql(`
  query fetchHubSpotOrganizationSource($id: bigint!) {
    source: hubspot_company_by_pk(id: $id) {
      id
      name: property_name
      description: property_description
      domain: property_domain
      industry: property_industry
      public: property_is_public
      foundedYear: property_founded_year
      annualRevenue: property_annualrevenue
      employeeCount: property_numberofemployees
      twitterHandle: property_twitterhandle
      facebookPage: property_facebook_company_page
      stage: property_lifecyclestage
    }
  }
`)

// Sanity specific queries
export const fetchSanityOrganizationSource = graphql(`
  query fetchSanityOrganizationSource($id: ID!) {
    source: sanity_Organization(id: $id) {
      name
      id: _id
      portfolioCompany
      location
      link
      description
      founded
      joined
      logo {
        asset {
          url
        }
      }
      resources {
        link
        title
      }
      stats {
        label
        value
      }
      status
      tags {
        ... on SanityTag {
          name
        }
      }
    }
  }
`)

export const fetchSanityPersonSource = graphql(`
  query fetchSanityPersonSource($id: ID!) {
    source: sanity_Person(id: $id) {
      name
      id: _id
      position
      organization {
        name
      }
      photo {
        asset {
          url
        }
      }
      tags {
        ... on SanityTag {
          name
        }
      }
    }
  }
`)

// Standard Metrics specific queries
export const fetchStandardMetricsOrganizationSource = graphql(`
  query fetchStandardMetricsOrganizationSource($id: String!) {
    source: standard_metrics_company_by_pk(id: $id) {
      id
      name
      slug
      website
      logo_url
      description
      sector
      firm_sector
      city
      status
      fiscal_year_end
      _fivetran_synced
    }
  }
`)

export const fetchStandardMetricsCompanyMetrics = graphql(`
  query fetchStandardMetricsCompanyMetrics($companyId: String!) {
    metrics: standard_metrics_metrics(
      where: { company_id: { _eq: $companyId }, _fivetran_deleted: { _eq: false } }
      order_by: [{ date: desc }, { category_id: asc }]
    ) {
      _fivetran_id
      category_id
      date
      value
      converted_value
      cadence
      detailed_source
      preferred_currency
      is_budget_metric
      updated_at
      metrics_option {
        category_name
        type
        is_point_in_time
      }
    }
  }
`)

export const fetchStandardMetricsFinancingRounds = graphql(`
  query fetchStandardMetricsFinancingRounds($companyId: String!) {
    financing_events: standard_metrics_financing_event(
      where: { company_id: { _eq: $companyId }, _fivetran_deleted: { _eq: false } }
      order_by: { closing_date: desc_nulls_last }
    ) {
      id
      name
      closing_date
      round_size_value
      round_size_currency
      pre_money_valuation_value
      pre_money_valuation_currency
      fully_diluted_shares
      co_investors(where: { _fivetran_deleted: { _eq: false } }, order_by: { index: asc }) {
        name
        is_lead
      }
    }
  }
`)

// Find unlinked person sources across all external systems
export const findUnlinkedPersonSources = graphql(`
  query findUnlinkedPersonSources($limit: Int = 100) {
    pipedrive: pipedrive_person(
      where: { _not: { record_source: {} } }
      limit: $limit
      order_by: { created_at: desc }
    ) {
      source_id: id
      name
      email
    }

    airtable: airtable_people(
      where: { _not: { record_source: {} } }
      limit: $limit
      order_by: { id: desc }
    ) {
      source_id: id
      name
      email: primary_email
    }

    hubspot: hubspot_contact(
      where: { _not: { record_source: {} } }
      limit: $limit
      order_by: { id: desc }
    ) {
      source_id: id
      name: full_name
      email
    }

    mailchimp: mailchimp_member(
      where: { _not: { record_source: {} } }
      limit: $limit
      order_by: { timestamp_signup: desc }
    ) {
      source_id: id
      name: full_name
      email: email_address
    }

    luma: luma_event_guest(
      where: { _not: { record_source: {} } }
      limit: $limit
      order_by: { created_at: desc }
    ) {
      source_id: user_api_id
      name: user_name
      email: user_email
    }

    slack: slack_users(
      where: { _not: { record_source: {} } }
      limit: $limit
      order_by: { id: desc }
    ) {
      source_id: id
      name: real_name
      email: profile_email
    }

    sanity: sanity_allPerson(limit: $limit) {
      source_id: _id
      name
    }
  }
`)

// Find unlinked organization sources across all external systems
export const findUnlinkedOrganizationSources = graphql(`
  query findUnlinkedOrganizationSources($limit: Int = 100) {
    pipedrive: pipedrive_organization(
      where: { _not: { record_source: {} } }
      limit: $limit
      order_by: { created_at: desc }
    ) {
      source_id: id
      name
      description: custom_short_description
      url: custom_url
    }

    airtable: airtable_organizations(
      where: { _not: { record_source: {} } }
      limit: $limit
      order_by: { id: desc }
    ) {
      source_id: id
      name: company_name
      description: short_description
      url: company_website
    }

    hubspot: hubspot_company(
      where: { _not: { record_source: {} } }
      limit: $limit
      order_by: { id: desc }
    ) {
      source_id: id
      name: property_name
      description: property_description
      url: property_domain
    }

    sanity: sanity_allOrganization(limit: $limit) {
      source_id: _id
      name
      description
    }

    standard_metrics: standard_metrics_company(
      where: { _and: [{ _fivetran_deleted: { _eq: false } }, { _not: { record_source: {} } }] }
      limit: $limit
      order_by: { name: asc }
    ) {
      source_id: id
      name
      description: sector
      url: website
    }
  }
`)

// Find unlinked event sources across all external systems
export const findUnlinkedEventSources = graphql(`
  query findUnlinkedEventSources($limit: Int = 100) {
    luma: luma_event(
      where: { _not: { record_source: {} } }
      limit: $limit
      order_by: { start_at: desc }
    ) {
      source_id: id
      name
      description
      url
      start_at
      end_at
    }

    # Note: airtable_projects and airtable_global_events_tracker don't have record_source
    # relationship, so we show all results (similar to sanity)
    airtable: airtable_projects(limit: $limit, order_by: { id: desc }) {
      source_id: id
      name: project
    }

    airtable_global: airtable_global_events_tracker(limit: $limit, order_by: { id: desc }) {
      source_id: id
      name: event
      description: event_description
    }

    sanity: sanity_allEvent(limit: $limit) {
      source_id: _id
      name: title
    }

    sanity_devguild: sanity_allDevGuild(limit: $limit) {
      source_id: _id
      name: title
    }
  }
`)

// Fetch email contacts from members of child lists (for transcripts on non-person records)
// Used to find transcripts where any member of an organization/event's lists participated
// Only fetches from lists where data.record_type = 'person' to ensure we get person records
export const fetchChildListMemberEmails = graphql(`
  query fetchChildListMemberEmails($recordId: Int!) {
    records_by_pk(id: $recordId) {
      id
      children(where: { type: { _eq: "list" }, data: { _contains: { record_type: "person" } } }) {
        id
        name
        members {
          id
          record {
            ...RequiredRecordFields
            contacts(where: { contact_type: { _eq: "email" } }) {
              id
              value
            }
          }
        }
      }
    }
  }
`)
